import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { GatheringFormDialog } from '@/components/programs/gathering-form-dialog';
import {
  type Gathering,
  type GatheringKind,
  detailPathFor,
  scheduleText,
} from '@/components/programs/gathering-schedule';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS, useCurrentMembership } from '@/lib/permissions';

export const GatheringsPage = ({ kind }: { kind: GatheringKind }) => {
  const { t } = useTranslation('programs');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const membership = useCurrentMembership();
  const canCreate = hasPermission(membership, PERMISSIONS.PROGRAMS_CREATE);
  const canEdit = hasPermission(membership, PERMISSIONS.PROGRAMS_EDIT);
  const canReorder = kind === 'service' && canEdit;
  const ns = kind === 'service' ? 'services' : 'programs';

  const [formOpen, setFormOpen] = useState(false);
  const [publishTarget, setPublishTarget] = useState<Gathering | null>(null);
  const [unpublishTarget, setUnpublishTarget] = useState<Gathering | null>(null);

  const list = useQuery({
    queryKey: ['programs', kind],
    queryFn: () => api.get<{ data: Gathering[] }>(`/programs?kind=${kind}&pageSize=100`),
  });

  useEffect(() => {
    if (searchParams.get('create') !== '1') return;
    if (membership === null) return;

    const next = new URLSearchParams(searchParams);
    next.delete('create');
    setSearchParams(next, { replace: true });

    if (canCreate) {
      setFormOpen(true);
    }
  }, [canCreate, membership, searchParams, setSearchParams]);

  const columns: DataTableColumn<Gathering>[] = [
    {
      id: 'name',
      header: t('columns.name'),
      cell: (row) => row.name,
    },
    {
      id: 'schedule',
      header: t('columns.schedule'),
      cell: (row) => scheduleText(row, t),
    },
    {
      id: 'venue',
      header: t('columns.venue'),
      cell: (row) => row.venueName ?? '—',
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('columns.status'),
      cell: (row) => (
        <Badge variant={row.isActive ? 'success' : 'neutral'}>
          {row.isActive ? t('detail.published') : t('detail.draft')}
        </Badge>
      ),
      hideBelowMd: true,
    },
  ];

  const rows = list.data?.data ?? [];

  const reorder = useMutation({
    mutationFn: (programIds: string[]) =>
      api.post<{ data: Gathering[] }>('/programs/services/reorder', { programIds }),
    onSuccess: (result) => {
      queryClient.setQueryData(['programs', kind], result);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const publish = useMutation({
    mutationFn: (id: string) => api.post<Gathering>(`/programs/${id}/publish`),
    onSuccess: () => {
      toast.success(t(`${ns}.published`));
      setPublishTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['programs'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const unpublish = useMutation({
    mutationFn: (id: string) => api.post<Gathering>(`/programs/${id}/unpublish`),
    onSuccess: () => {
      toast.success(t(`${ns}.unpublished`));
      setUnpublishTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['programs'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  return (
    <div>
      <PageHeader
        title={t(`${ns}.title`)}
        description={t(`${ns}.description`)}
        tipId="programs.occurrenceVsProgram"
        headerActions={
          canCreate ? (
            <Button onClick={() => setFormOpen(true)}>{t(`${ns}.create`)}</Button>
          ) : undefined
        }
      />
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={list.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t(`${ns}.empty`)}</p>}
        onRowClick={(row) => void navigate(detailPathFor(kind, row.id))}
        rowActions={
          canEdit
            ? (row) =>
                row.isActive ? (
                  <Button size="sm" variant="secondary" onClick={() => setUnpublishTarget(row)}>
                    {t('unpublish')}
                  </Button>
                ) : (
                  <Button size="sm" onClick={() => setPublishTarget(row)}>
                    {t('publish')}
                  </Button>
                )
            : undefined
        }
        reorder={
          canReorder
            ? {
                handleLabel: tCommon('action.dragToReorder'),
                disabled: reorder.isPending,
                onReorder: async (programIds) => {
                  await reorder.mutateAsync(programIds);
                },
              }
            : undefined
        }
      />

      <GatheringFormDialog kind={kind} open={formOpen} onOpenChange={setFormOpen} />

      <ConfirmDialog
        open={publishTarget !== null}
        onOpenChange={(next) => {
          if (!next) setPublishTarget(null);
        }}
        title={t(`confirm.publish.title.${kind}`, { name: publishTarget?.name ?? '' })}
        description={t(`confirm.publish.description.${kind}`)}
        confirmLabel={t(`confirm.publish.confirm.${kind}`)}
        cancelLabel={tCommon('action.cancel')}
        loading={publish.isPending}
        onConfirm={() => {
          if (publishTarget) publish.mutate(publishTarget.id);
        }}
      />

      <ConfirmDialog
        open={unpublishTarget !== null}
        onOpenChange={(next) => {
          if (!next) setUnpublishTarget(null);
        }}
        title={t(`confirm.unpublish.title.${kind}`, { name: unpublishTarget?.name ?? '' })}
        description={t(`confirm.unpublish.description.${kind}`)}
        confirmLabel={t(`confirm.unpublish.confirm.${kind}`)}
        cancelLabel={tCommon('action.cancel')}
        loading={unpublish.isPending}
        onConfirm={() => {
          if (unpublishTarget) unpublish.mutate(unpublishTarget.id);
        }}
      />
    </div>
  );
};
